import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { showPaywall } from "@/lib/paywall";

// Shared "followed riders" store. One source of truth so the My Riders page and the
// Map rider mode stay in sync (broadcast to every mounted consumer).
let cache = null;
let mutated = false;
const listeners = new Set();
function broadcast(list) { cache = list; listeners.forEach((fn) => fn(list)); }

export default function useFollowing() {
  const [following, setFollowing] = useState(cache || []);
  useEffect(() => {
    const fn = (l) => setFollowing([...l]);
    listeners.add(fn);
    if (cache === null) {
      cache = []; // prevent duplicate fetches
      // Don't clobber an optimistic follow/unfollow that happened while this was in flight.
      api.get("/competitors/following").then((r) => { if (!mutated) broadcast(r.data || []); }).catch(() => { if (!mutated) broadcast([]); });
    } else {
      setFollowing(cache);
    }
    return () => listeners.delete(fn);
  }, []);

  const isFollowing = useCallback((name) => (cache || []).some((n) => n.toLowerCase() === String(name || "").toLowerCase()), [following]);

  const follow = useCallback(async (name) => {
    if ((cache || []).some((n) => n.toLowerCase() === name.toLowerCase())) return;
    mutated = true;
    broadcast([...(cache || []), name]);
    try {
      const { data } = await api.post("/competitors/follow", { name });
      toast.success(data?.charged ? `Following ${name} · ${data.charged} credit used` : `Following ${name}`, { duration: 2500 });
      window.dispatchEvent(new Event("rp-credits-refresh"));
    } catch (e) {
      broadcast((cache || []).filter((n) => n !== name));
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && d) {
        showPaywall({ ...d, feature: "follow" });
      } else {
        toast.error("Couldn't follow — try again");
      }
    }
  }, []);

  const unfollow = useCallback(async (name) => {
    const prev = cache || [];
    mutated = true;
    broadcast(prev.filter((n) => n !== name));
    try { await api.delete(`/competitors/follow?name=${encodeURIComponent(name)}`); }
    catch { broadcast(prev); toast.error("Couldn't unfollow — try again"); }
  }, []);

  return { following, isFollowing, follow, unfollow };
}
